import { CommonDialogs } from "@mcleod/common";
import { Button, ButtonVariant, ChangeEvent, Checkbox, ClickEvent, Component, ContextMenuLabelProps, Cursor, DataSource, DataSourceRemoteDataChangeEvent, Image, Label, ModelDataChangeType, Panel, SelectionType, Snackbar, Table, TableRow, TableRowDisplayEvent, TableRowExpansionEvent, Toast } from "@mcleod/components";
import { LabelStyles } from "@mcleod/components/src/components/label/LabelStyles";
import { SelectCheckboxHeadingTableCell } from "@mcleod/components/src/components/table/SelectCheckboxHeadingTableCell";
import { SelectCheckboxTableCell } from "@mcleod/components/src/components/table/SelectCheckboxTableCell";
import { Api, CompanySettings, LogManager, ModelRow, Navigation, ServerError, StringUtil, VerticalAlignment } from "@mcleod/core";
import { LMEUserSettings } from "@mcleod/general";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutPartialShipmentPlanningSplitView } from "./autogen/AutogenLayoutPartialShipmentPlanningSplitView";
import { ModelManifestProfile, RowManifestProfile } from "./models/ModelManifestProfile";
import { PartialShipmentManifestCreationSlideout } from "./PartialShipmentManifestCreationSlideout";
import { PartialShipmentManifestDetails } from "./PartialShipmentManifestDetails";
import { PartialShipmentPlanningFilter } from "./PartialShipmentPlanningFilter";
import { PartialShipmentPlanningUtil } from "./PartialShipmentPlanningUtil";
import { FilterPanel, SecondaryFilterProps } from "./planning/FilterPanel";
import { PlanningUtil } from "./planning/PlanningUtil";
import { YSplit } from "./YSplit";

const log = LogManager.getLogger("PartialShipmentPlanning");
const UNSAVED_STOPS_TOOLTIP = "Please save stop changes before viewing pick and drop details.";

export class PartialShipmentPlanningSplitView extends AutogenLayoutPartialShipmentPlanningSplitView {

    public ordersFilterPanel: FilterPanel;
    public manifestFilterPanel: FilterPanel;
    private _profileRow: RowManifestProfile;
    private filterObject: PartialShipmentPlanningFilter;
    private showErrorToastOrders: boolean;
    private buttonCreateManifest: Button;
    private buttonOrdersMap: Button;
    private holdManifestImage: Image;
    private crossDockImage: Image;

    public ordersSecondaryFilters: SecondaryFilterProps[] = PartialShipmentPlanningUtil.getOrdersSecondaryFilters();
    public manifestsSecondaryFilters: SecondaryFilterProps[] = PartialShipmentPlanningUtil.getManifestsSecondaryFilters();

    override onLoad() {
        this.setupHeadersAndSplitScreen50Percent();
        this.addFiltersInPartialShipmentPlanning();
        this.executeDrillDownToOrderDetails();
        this.setupOrdersTable();
        this.addActionItemToOrdersTable();
        this.addActionItemToManifestsTable();
    }

    private addActionItemToManifestsTable() {
        this.tableManifests.addSelectionListener(() => this.updateImageTooltipAndState());
        this.holdManifestImage = new Image({
            name: ImageName.onHold, fontSize: '16', color: 'default.lightest',
            enabled: false, marginRight: 8,
            onClick: () => { this.addHoldRemoveManifest(); }
        });
        this.crossDockImage = new Image({
            name: ImageName.crossDock, fontSize: '16', color: 'default.lightest',
            enabled: false, marginRight: 8,
            onClick: () => this.crossDockManifest()
        });
        this.buttonCreateManifest = new Button({
            caption: "Create Shell Manifest", color: "primary.reverse",
            backgroundColor: "primary", onClick: () => this.showCreateManifestSlideOut()
        });
        this.tableManifests.addCenterTool(this.holdManifestImage);
        this.tableManifests.addCenterTool(this.crossDockImage);
        this.tableManifests.addRightTool(this.buttonCreateManifest);
    }

    private addActionItemToOrdersTable() {
        this.buttonOrdersMap = new Button({
            imageName: ImageName.designer_map, imageColor: "default.lightest", variant: ButtonVariant.round, enabled: false,
            disabledTooltip: "One or more orders must be selected to view map.", onClick: () => this.openOrdersMap(),
            imageHeight: 26, imageWidth: 26
        });
        this.tableOrders.addCenterTool(this.buttonOrdersMap);
    }

    updateImageTooltipAndState() {
        const selectedRow = this.tableManifests.selectedRow;
        if (selectedRow) {
            const modelRow = selectedRow.data as ModelRow;
            const manifestHold: boolean = modelRow.get("manifest_hold");
            const movementId: string = modelRow.get("movement_id");
            if (!StringUtil.isEmptyString(movementId)) {
                const manifestId: string = modelRow.get("manifest_id");
                const tooltip = manifestHold ? `Remove Hold from ${manifestId}` : `Apply Hold to ${manifestId}`;
                this.holdManifestImage.color = 'primary';
                this.holdManifestImage.enabled = true;
                this.holdManifestImage.tooltip = tooltip;
                this.holdManifestImage.cursor = Cursor.POINTER;
                if (!manifestHold) {
                    this.crossDockImage.enabled = true;
                    this.crossDockImage.color = 'primary';
                    this.crossDockImage.tooltip = "Cross Dock Manifest " + manifestId;
                    this.crossDockImage.cursor = Cursor.POINTER;
                } else {
                    this.crossDockImage.enabled = false;
                    this.crossDockImage.tooltip = null;
                    this.crossDockImage.color = "default.lightest";
                    this.crossDockImage.cursor = Cursor.DEFAULT;
                }
            } else {
                this.resetTableManifestRowAndIcons();
            }
        }
    }

    addHoldRemoveManifest(): void {
        const currentSelectedRow: TableRow = this.tableManifests.selectedRow;
        const modelRow = currentSelectedRow.data as ModelRow;
        const manifestHold = modelRow.get("manifest_hold");
        this.managePartialShipmentHold(this.tableManifests, currentSelectedRow, modelRow, !manifestHold);

    }

    crossDockManifest(): void {
        const currentSelectedRow: TableRow = this.tableManifests.selectedRow;
        this.crossDock(currentSelectedRow);
    }

    private openOrdersMap(): void {
        const movementIds: string[] = this.getSelectedIds("movement_id");
        const url  = "lme/dispatch/PartialShipmentPlanningAvailableOrdersMap?movement_ids=" + movementIds.join(",");
        Navigation.navigateTo(url, { newTab: true });
        window.focus();
    }

    setupOrdersTable() {
        this.tableOrders.addSelectionListener(() => this.updateOrdersSelection());
        const tableColumCheckBox = this.tableOrders.columns.find(col => col.headingCell instanceof SelectCheckboxHeadingTableCell);
        if (tableColumCheckBox != null) {
            (tableColumCheckBox.headingCell as SelectCheckboxHeadingTableCell).checkbox.visible = false;
        }
    }

    updateOrdersSelection() {
        if (this.tableOrders.selectedRows.length > 0) {
            this.buttonCreateManifest.caption = "Create Manifest";
            this.buttonOrdersMap.enabled = true;
            this.buttonOrdersMap.imageColor = "primary";
            this.buttonOrdersMap.tooltip = "Map selected orders"
        } else {
            this.buttonCreateManifest.caption = "Create Shell Manifest";
            this.buttonOrdersMap.enabled = false;
            this.buttonOrdersMap.imageColor = "default.lightest";
            this.buttonOrdersMap.tooltip = null;
        }
        this.manageAddOrdersToManifestVisibility();
    }

    private addFiltersInPartialShipmentPlanning() {
        this.ordersFilterPanel = new FilterPanel(this.ordersSecondaryFilters, this.tableOrders, this.panelOrdersFilter);
        this.manifestFilterPanel = new FilterPanel(this.manifestsSecondaryFilters, this.tableManifests, this.panelManifestsFilter);
        this.ordersFilterPanel.panel.scrollX = true;
        this.manifestFilterPanel.panel.scrollX = true;
    }

    private executeDrillDownToOrderDetails() {
        this.tableOrders.overrideUrlParams = (selectedRow: TableRow) => {
            return { key: selectedRow.data.get("movement_id"), orderId: selectedRow.data.get("order_id") };
        };
    }

    /** This function sets the background color and text color for the Splitter Header. */
    private setupHeadersAndSplitScreen50Percent() {
        this.splitterPartialShipmentPlanningView.position = "50%";
        const { header_bg_color: bgColor, text_color: textColor } = CompanySettings.get() ?? {};
        PartialShipmentPlanningUtil.setPanelColors(this.panelOrdersContainer, this.panelOrdersStrip, this.labelOrders, bgColor, textColor);
        PartialShipmentPlanningUtil.setPanelColors(this.panelManifestsContainer, this.panelManifestsStrip, this.labelManifests, bgColor, textColor);
    }

    /** This is an event for tableOrdersOnRowDisplay */
    tableOrdersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow() as TableRow;
        const modelRow = tableRow.data as ModelRow;
        PartialShipmentPlanningUtil.setupOrderIdLabel(tableRow, modelRow);
        PartialShipmentPlanningUtil.showBadgesInOrdersSection(tableRow, modelRow);
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelOriginScheduleLateTime", "origin_schedule_late");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelDestinationScheduleLateTime", "destination_schedule_late");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelWeight", "weight");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelPallets", "pallets");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelOrderLength", "order_length");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelMiles", "move_distance");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "labelStopNameOrigin", "origin_stop_name");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "labelStopNameDestination", "destination_stop_name");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "labelCommodity", "commodity");
        PartialShipmentPlanningUtil.formatMiles(tableRow, modelRow);
        PartialShipmentPlanningUtil.formatWeight(tableRow, modelRow);
        PartialShipmentPlanningUtil.setupApptRequiredConfirmed(tableRow, modelRow, "imageOriginApptRequireConfirmed", "origin");
        PartialShipmentPlanningUtil.setupApptRequiredConfirmed(tableRow, modelRow, "imageDestinationApptRequireConfirmed", "destination");
        PartialShipmentPlanningUtil.showHideInterimStops(tableRow, modelRow);
        PartialShipmentPlanningUtil.showMutilStopSliderFromOrders(tableRow);
        PartialShipmentPlanningUtil.showOrderHoldBadgeAndReason(tableRow, modelRow);
        tableRow.forEveryChildComponent(comp => PartialShipmentPlanningUtil.setTooltipContent(comp, modelRow));
        tableRow.contextMenuItems = () => PartialShipmentPlanningUtil.addContextMenuItemOrdersTable(this.tableOrders, tableRow, modelRow);
        this.setCheckBoxStyle(tableRow)
    }

    setCheckBoxStyle(tableRow: TableRow) {
        for (const cell of tableRow.cells) {
            if (cell instanceof SelectCheckboxTableCell) {
                cell.verticalAlign = VerticalAlignment.TOP;
                cell.checkbox.addChangeListener((event: ChangeEvent) => {
                    if (event.target instanceof Checkbox && event.target.checked === true) {
                      cell.checkbox.checkColor = "primary";
                    }
                    else {
                      cell.checkbox.checkColor = "default.lightest";
                    }
                });
                if (cell.checkbox.checked == false) {
                    cell.checkbox.checkColor = "default.lightest";
                }
                break;
            }
        }
    }

    /** This is an event for tableManifestsOnRowDisplay */
    tableManifestsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow() as TableRow;
        const modelRow = tableRow.data as ModelRow;
        PartialShipmentPlanningUtil.showShellManifestIcon(modelRow, tableRow);
        PartialShipmentPlanningUtil.setupManifestIdLabel(tableRow);
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelManifestsPickDrops", "movement_id");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelOriginScheduleLateTime", "origin_schedule_late");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelDestinationScheduleLateTime", "destination_schedule_late");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelWeight", "weight");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelPallets", "pallets");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelOrderLength", "order_length");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "panelMiles", "move_distance");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "labelStopNameOrigin", "origin_stop_name");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "labelStopNameDestination", "destination_stop_name");
        PartialShipmentPlanningUtil.showHideComponent(tableRow, "textboxCommdities", "manifest_commodities");
        PartialShipmentPlanningUtil.manifestCommodities(tableRow, modelRow);
        PartialShipmentPlanningUtil.formatMiles(tableRow, modelRow);
        PartialShipmentPlanningUtil.formatWeight(tableRow, modelRow);
        PartialShipmentPlanningUtil.showBadgesInManifestsSection(tableRow, modelRow);
        PartialShipmentPlanningUtil.setupApptRequiredConfirmed(tableRow, modelRow, "imageOriginApptRequireConfirmed", "origin");
        PartialShipmentPlanningUtil.setupApptRequiredConfirmed(tableRow, modelRow, "imageDestinationApptRequireConfirmed", "destination");
        PartialShipmentPlanningUtil.showHideInterimStops(tableRow, modelRow);
        PartialShipmentPlanningUtil.showMutilStopSliderFromManifests(tableRow);
        tableRow.forEveryChildComponent(comp => PartialShipmentPlanningUtil.setTooltipContent(comp, modelRow));
        tableRow.contextMenuItems = () => this.addContextMenuItemManifestTable(this.tableManifests, tableRow, modelRow);
    }

    labelOrderIdOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    labelManifestIdOnClick(event: ClickEvent) {
        const tableRow: TableRow = TableRow.getContainingTableRow(event.target as Component);
        Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + tableRow.data.get("manifest_id"), { newTab: true });
    }

    // loadProfile Method call >> from MultiProfilePlanningTabs.ts
    async loadProfile(profile?: string | ModelRow) {
        if (profile instanceof ModelRow) {
            this.setProfileRow(profile);
            return;
        }
        const profileId = profile ?? LMEUserSettings.get()?.manifest_profile;
        if (profileId == null)
            Snackbar.showSnackbar("A default Partial Shipment Planning Profile is not set. One can be set via User Settings.", { persist: true });
        else {
            const profileRow = await new ModelManifestProfile().searchSingle({ id: profileId });
            if (profileRow == null) {
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", { persist: true });
            } else {
                await this.setProfileRow(profileRow).then (() => this.manageAddOrdersToManifestVisibility());
            }
        }
    }

    public get profileRow(): ModelRow { return this._profileRow; }

    public async setProfileRow(value: ModelRow) {
        if (value != this._profileRow) {
            this._profileRow = value;
            this.filterObject = new PartialShipmentPlanningFilter(value);
            if (this._profileRow != null) {
                this.sourcePartialShipmentPlanningOrders.search({ profile_row: this.profileRow });
                this.sourcePartialShipmentPlanningManifests.search({ profile_row: this.profileRow });
            }
        }
    }

    /** This is an event handler for the onSubscriberError event of sourcePartialShipmentPlanningOrders.  */
    onSubscribeError(event) {
        if (!this.showErrorToastOrders) {
            this.showErrorToastOrders = true;
            const toastOptions = { persist: true, onDismiss: () => this.showErrorToastOrders = false };
            Toast.showToast("There was a problem keeping this page up-to-date real time. Try refreshing the page.", null, toastOptions);
        }
    }

    /** This is an event handler for the onRemoteDataChange event of sourcePartialShipmentPlanningOrders.  */
    handleRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let eventChangeType = event.change.type;
        const dataSource = event.target as DataSource;
        const isOrderDataSource = dataSource === this.sourcePartialShipmentPlanningOrders;
        log.info(`Remote data change event: ${eventChangeType} for ${isOrderDataSource ? "Order DataSource" : "Manifest DataSource"}`);
        const changedRow = event.change.data as ModelRow;
        const index = dataSource.findRowInData(changedRow);
        if (this.shouldHideRow(changedRow, eventChangeType, isOrderDataSource)) {
            eventChangeType = ModelDataChangeType.DELETE;
        }
        if (eventChangeType === ModelDataChangeType.ADD && index >= 0) {
            eventChangeType = ModelDataChangeType.UPDATE;
        }
        let changedValues = {};
        if (eventChangeType == ModelDataChangeType.UPDATE && changedRow != null) {
            changedValues = changedRow.compareWithData(changedRow.get("originalData"));
        }
        if (isOrderDataSource) {
            this.sourcePartialShipmentPlanningOrders.handleDataChange({ type: eventChangeType, data: changedRow });
        } else {
            this.sourcePartialShipmentPlanningManifests.handleDataChange({ type: eventChangeType, data: changedRow });
        }
        event.preventDefault();
    }

    private shouldHideRow(changedRow: ModelRow, eventChangeType: ModelDataChangeType, isOrderDataSource: boolean): boolean {
        const shouldHide = this.filterObject?.shouldHideRow(changedRow, isOrderDataSource);
        return eventChangeType !== ModelDataChangeType.DELETE && shouldHide;
    }

    /** This is an event handler for the onRowExpand event of tableManifests. */
    async tableManifestsOnRowExpand(event: TableRowExpansionEvent) {
        const tableRow = event.getTableRow() as TableRow;
        const modelRow = tableRow.data as ModelRow;
        const manifestId = modelRow.get("manifest_id");
        const movementId = modelRow.get("movement_id");
        const expandPanel = event.expandComponentParent as Panel;
        const manifestDetails = expandPanel.findComponentById("layoutPartialShipmentManifestDetails") as PartialShipmentManifestDetails;
        manifestDetails.manifestId = manifestId;
        manifestDetails.movementId = movementId;
        manifestDetails.onStopDetailsChange = (hasUnsavedChanges: boolean) => {
            this.updateManifestPickDropInteractivity(tableRow, modelRow, hasUnsavedChanges);
        };
        this.updateManifestPickDropInteractivity(tableRow, modelRow, manifestDetails.sourcePartialShipmentManifestStopDetails.hasChanged());
        if (modelRow.get("is_shell_manifests") === false) {
            await manifestDetails.sourcePartialShipmentManifestStopDetails.search({ id: movementId, operation: "expand" });
            await manifestDetails.sourcePartialShipmentManifestOrderDetails.search({ planned_manifest_id: manifestId });
            await manifestDetails.sourcePartialShipmentPlanningManifestProratedRevenue.search({ movement_id: movementId });
        }
    }

    private updateManifestPickDropInteractivity(tableRow: TableRow, modelRow: ModelRow, hasUnsavedChanges: boolean) {
        const panelPickDrops = tableRow.findComponentById("panelManifestsPickDrops") as Panel;
        if (panelPickDrops == null) {
            return;
        }

        panelPickDrops.enabled = !hasUnsavedChanges;
        panelPickDrops.cursor = hasUnsavedChanges ? Cursor.DEFAULT : Cursor.POINTER;
        panelPickDrops.tooltip = hasUnsavedChanges ? UNSAVED_STOPS_TOOLTIP : null;
        panelPickDrops.tooltipDynamicContent = hasUnsavedChanges ? null : panelPickDrops.tooltipDynamicContent;
        panelPickDrops.forEveryChildComponent((component: Component) => {
            component.cursor = hasUnsavedChanges ? Cursor.DEFAULT : Cursor.POINTER;
            component.style.pointerEvents = hasUnsavedChanges ? "none" : "";
            if (component instanceof Label) {
                component.color = hasUnsavedChanges ? "default.lightest" : "primary";
                component.setClassIncluded(LabelStyles.link, false);
            }
        });

        if (!hasUnsavedChanges) {
            PartialShipmentPlanningUtil.setTooltipContent(panelPickDrops, modelRow);
        }
    }

    showCreateManifestSlideOut() {
        const movementIds :string[] = [];
        const selectedRows : TableRow[] = this.tableOrders.selectedRows;
        selectedRows.forEach(row => { movementIds.push(row.data.get("movement_id"));});
        PartialShipmentManifestCreationSlideout.showSlideout({
            movement_ids: movementIds,
            selectedRows: selectedRows,
            table: this.tableOrders,
            doAfterSlideOut: async (success: boolean = false, manifestId: string) => {
                if (success) {
                    Toast.showSuccessToast(`Partial Shipment Manifest ${manifestId} Created`);
                    this.tableOrders.selectedRows = [];
                    this.buttonCreateManifest.caption = "Create Shell Manifest";
                    this.manageAddOrdersToManifestVisibility();
                }
            }
        });
    }

    public manageAddOrdersToManifestVisibility() {
        const selectedOrderIds = this.getSelectedIds("order_id");
        const ordersAreSelected: boolean = (selectedOrderIds.length > 0);
        const tooltipLabel = new Label({ padding: 0, margin: 0, rowBreak: false, fillRow: true, scrollY: true, maxWidth: 300, maxHeight: 400 });
        this.tableManifests.rows.forEach((row: TableRow) => {
            const addOrdersImage = row.findComponentById("imageAddOrders") as Image;
            if (addOrdersImage) {
                addOrdersImage.visible = ordersAreSelected;
                if (ordersAreSelected) {
                    const isSingleOrder = selectedOrderIds.length === 1;
                    const titleText = `Add Order${isSingleOrder ? '' : 's'} to this Manifest`;
                    const orderList = selectedOrderIds.map((orderId) => `${orderId}`).join('\n');
                    tooltipLabel.text = `${titleText}\n\n${orderList}`;
                    addOrdersImage.tooltip = tooltipLabel;
                }
            }
        });
    }

    getSelectedIds(columnName: String) {
        const ids :string[] = [];
        this.tableOrders.selectedRows.forEach(row => {
            ids.push(row.data.get(columnName));
        });
        return ids;
    }

    clearSelectedOrders() {
        this.tableOrders.selectedRows = []
    }

    setOperationalControlsEnabled(value: boolean) {
        this.buttonCreateManifest.enabled = value;
        for (const tableRow of this.tableOrders.rows) {
            const image: Image = tableRow.findComponentById("imageOrderSelection") as Image;
            if (image) {
                image.enabled = value;
            }
        }
        for (const tableRow of this.tableManifests.rows) {
            const image: Image = tableRow.findComponentById("imageAddOrders") as Image;
            if (image) {
                image.enabled = value;
            }
        }
    }

    scrollToManifestAndExpand(manifestId: String, movementId: String) {
        this.tableManifests.rows.forEach(row => {
            if (row.data.get("manifest_id") === manifestId && row.data.get("movement_id") === movementId) {
                row.scrollIntoView();
                row.setExpandedAsync(true);
            }
        });
    }

    async imageAddOrdersOnClick(event: ClickEvent) {
        const addOrdersImage = event.target as Image;
        const tableRow = TableRow.getContainingTableRow(addOrdersImage);
        this.setOperationalControlsEnabled(false);
        tableRow.expanded = false;
        const manifestId = tableRow.data.get("manifest_id");
        const movementId = tableRow.data.get("movement_id");
        addOrdersImage.busy = true;

        try {
            const response = await Api.update("lme/dispatch/manifest-add-orders", {
                movement_id_list: this.getSelectedIds("movement_id"),
                manifest_id: manifestId,
                manifest_movement_id: tableRow.data.get("movement_id")
            });
            const resultsCount = response.data[0].results_count;
            const isSuccess = response.data[0].success;
            if (!isSuccess) {
                Snackbar.showWarningSnackbar("An error occurred while trying to add orders to the manifest. Please try again.");
                addOrdersImage.busy = false;
                this.setOperationalControlsEnabled(true);
                return;
            }
            Toast.showToast(`Added ${resultsCount} order(s) to manifest ${manifestId}`);
            this.clearSelectedOrders();
            this.manageAddOrdersToManifestVisibility();
            addOrdersImage.busy = false;
            this.setOperationalControlsEnabled(true);
            if (this.profileRow != null) {
                const response = this.sourcePartialShipmentPlanningOrders.search({ profile_row: this.profileRow });
                if (response != null) {
                    this.scrollToManifestAndExpand(manifestId, movementId);
                }
            }
        } catch (error) {
            if (error instanceof ServerError) {
                CommonDialogs.showError(error, "Error Adding Orders to Manifest");
            } else {
                Snackbar.showWarningSnackbar(error, { persist: true });
            }
            addOrdersImage.busy = false;
            this.setOperationalControlsEnabled(true);
            return;
        }
    }

    public addContextMenuItemManifestTable(table: Table, tableRow: TableRow, modelRow: ModelRow): Partial<import("@mcleod/components").ContextMenuLabelProps>[] {
        if (table.selectedRow !== tableRow) {
            table.selectedRow = tableRow;
        }
        const manifestHold: boolean = modelRow.get("manifest_hold");
        const movementId: string = modelRow.get("movement_id");
        const menuItems: Partial<import("@mcleod/components").ContextMenuLabelProps>[] = [];
        if (!StringUtil.isEmptyString(movementId)) {
            if (!manifestHold) {
                menuItems.push(this.createActionEventNavigationItem(ImageName.onHold, "Apply Hold", () => this.managePartialShipmentHold(table, tableRow, modelRow, true), false));
                menuItems.push({text: "Cross Dock", onClick: () => this.crossDock(tableRow), fontSize: '16', imageName: ImageName.crossDock, separatorAfter: true});
            } else {
                menuItems.push(this.createActionEventNavigationItem(ImageName.onHold, "Remove Hold", () => this.managePartialShipmentHold(table, tableRow, modelRow, false), true));
            }
        }
        const mainUrl = "lme/dispatch/OrderList?manifest_id=" + modelRow.get("manifest_id");
        menuItems.push({text: "Open Manifest in New Tab", onClick: () => Navigation.navigateTo(mainUrl, { newTab: true })});
        menuItems.push({text: "Open Manifest in New Window", onClick: () => Navigation.navigateTo(mainUrl, { newTab: true, windowDecorators: false })});
        return menuItems;
    }

    private createActionEventNavigationItem(imageName: ImageName, text: string, onClickEvent: () => Promise<void> | void, separatorAfter: boolean = false): Partial<ContextMenuLabelProps> {
        return {text: text, onClick: onClickEvent, visible: true, imageName, separatorAfter: separatorAfter}
    }

    public  async managePartialShipmentHold(table: Table, tableRow: TableRow, modelRow: ModelRow, applyHold: boolean) {
        const movementId = modelRow.get("movement_id");
        const manifestId = modelRow.get("manifest_id");
        const response = await Api.post("lme/dispatch/partial-shipment-manifest-hold", {"movement_id": movementId, "manifest_id": manifestId, [applyHold ? "apply_hold" : "remove_hold"]: true})
        const data = response?.data[0];
        Toast.showToast(data.message);
        this.tableManifests.selectRow(tableRow, SelectionType.SINGLE, null);
        this.resetTableManifestRowAndIcons();
    }

    public crossDock(tableRow: TableRow): void {
        const companySettings = CompanySettings.get();
        const isAsset = companySettings?.is_asset;
        const isBrokerage = companySettings?.is_brokerage;
        const movementData = tableRow.data;
        const orderId: string = movementData.get("stop.order_id") as string;
        const manifestId: string = movementData.get("manifest_id") as string;
        const movementId: string = movementData.get("movement_id") as string;
        const movementSequence: string = movementData.get("stop.movement_sequence") as string;
        YSplit.showSlideout({
            orderId: orderId,
            isCompanyBoth: isAsset && isBrokerage,
            manifestId: manifestId,
            movementId: movementId,
            movementSequence: movementSequence,
            crossDock: true,
            doAfterSlideOut: async (success: boolean = false) => {
                if (success) {
                    Toast.showSuccessToast("Cross Dock created successfully");
                    if (tableRow.expanded) {
                        tableRow.expanded = !tableRow.expanded;
                    }
                }
            }
        });
        this.tableManifests.selectRow(tableRow, SelectionType.SINGLE, null);
        this.resetTableManifestRowAndIcons();
    }

    private resetTableManifestRowAndIcons() {
        this.holdManifestImage.tooltip = null;
        this.holdManifestImage.enabled = false;
        this.holdManifestImage.color = "default.lightest";
        this.holdManifestImage.cursor = Cursor.DEFAULT;
        this.crossDockImage.tooltip = null;
        this.crossDockImage.enabled = false;
        this.crossDockImage.color = "default.lightest";
        this.crossDockImage.cursor = Cursor.DEFAULT;
    }

    override get windowTitle(): string {
        const title = "Partial Shipment Planning";
        const profileId = this.profileRow?.getString("id");
        return profileId ? `${title} - ${profileId}` : title;
    }
}
